import { Activity, Award, Minus, Target, TrendingDown } from "lucide-react";

import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { useBrierSummary, useBrierStatus } from "@/sdk/hooks/use-alpha";
import type { BrierAgentScore } from "@/sdk/hooks/use-alpha";

function qualityColor(quality: string): string {
  switch (quality) {
    case "exceptional":
      return "text-emerald-400";
    case "excellent":
      return "text-green-400";
    case "good":
      return "text-blue-400";
    case "fair":
      return "text-amber-400";
    case "poor":
      return "text-red-400";
    default:
      return "text-white/40";
  }
}

function qualityBadge(quality: string): string {
  switch (quality) {
    case "exceptional":
      return "bg-emerald-500/20 text-emerald-300";
    case "excellent":
      return "bg-green-500/20 text-green-300";
    case "good":
      return "bg-blue-500/20 text-blue-300";
    case "fair":
      return "bg-amber-500/20 text-amber-300";
    case "poor":
      return "bg-red-500/20 text-red-300";
    default:
      return "bg-white/10 text-white/40";
  }
}

function brierTone(score: number | null): "default" | "success" | "warning" | "danger" {
  if (score === null) return "default";
  if (score <= 0.10) return "success";
  if (score <= 0.25) return "warning";
  return "danger";
}

function AgentRow({ agent }: { agent: BrierAgentScore }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-white/5 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-medium text-white/80 capitalize truncate">
          {agent.name}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-[0.65rem] font-semibold ${qualityBadge(agent.quality)}`}
        >
          {agent.quality}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs text-white/50">{agent.predictions} pred</span>
        <span className={`text-sm font-mono font-bold ${qualityColor(agent.quality)}`}>
          {agent.brier_score !== null ? agent.brier_score.toFixed(3) : "---"}
        </span>
      </div>
    </div>
  );
}

export function BrierScoreWidget() {
  const { data: summary, isLoading: summaryLoading } = useBrierSummary(30);
  const { data: status } = useBrierStatus();

  const overall = summary?.overall;
  const agents = summary?.agents ?? [];
  const benchmark = summary?.benchmark;

  return (
    <Panel
      title="Brier Score"
      description="Prediction accuracy tracker"
      actions={
        status?.status === "active" ? (
          <div className="flex items-center gap-1.5 text-xs text-white/40">
            <Activity className="h-3 w-3" />
            {status.pending_count} pending
          </div>
        ) : null
      }
    >
      {summaryLoading ? (
        <LoadingState text="Loading accuracy..." />
      ) : !overall || overall.total_predictions === 0 || overall.brier_score == null ? (
        <div className="py-6 text-center">
          <Target className="mx-auto h-8 w-8 text-white/20" />
          <p className="mt-2 text-sm text-white/40">No resolved predictions yet.</p>
          <p className="text-xs text-white/25">
            Brier scores will appear once markets resolve.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Overview stat cards */}
          <div className="grid grid-cols-3 gap-2">
            <StatCard
              label="Brier Score"
              value={overall.brier_score !== null ? overall.brier_score.toFixed(3) : "---"}
              hint={overall.quality !== "unknown" ? overall.quality : undefined}
              tone={brierTone(overall.brier_score)}
            />
            <StatCard
              label="Accuracy"
              value={
                overall.accuracy_rate !== null
                  ? `${(overall.accuracy_rate * 100).toFixed(1)}%`
                  : "---"
              }
              hint={`${overall.total_predictions} predictions`}
              tone={
                overall.accuracy_rate !== null && overall.accuracy_rate >= 0.7
                  ? "success"
                  : overall.accuracy_rate !== null && overall.accuracy_rate >= 0.5
                    ? "warning"
                    : "default"
              }
            />
            <StatCard
              label="vs Random"
              value={
                overall.brier_score !== null && benchmark
                  ? overall.brier_score < benchmark.random_guess
                    ? `${((1 - overall.brier_score / benchmark.random_guess) * 100).toFixed(0)}% better`
                    : "Worse"
                  : "---"
              }
              hint={`Random = ${benchmark?.random_guess ?? 0.25}`}
              tone={
                overall.brier_score === null || !benchmark
                  ? "default"
                  : overall.brier_score < benchmark.random_guess
                    ? "success"
                    : "danger"
              }
            />
          </div>

          {/* Agent leaderboard */}
          {agents.length > 0 ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs text-white/50">
                <Award className="h-3 w-3" />
                Agent Leaderboard
              </div>
              <div className="space-y-1">
                {agents.map((agent) => (
                  <AgentRow key={agent.name} agent={agent} />
                ))}
              </div>
            </div>
          ) : null}

          {/* Benchmark reference */}
          {benchmark ? (
            <div className="flex items-center justify-between rounded border border-white/5 bg-white/3 px-3 py-2 text-xs text-white/35">
              <span>Benchmarks:</span>
              <span className="flex gap-3">
                <span>
                  <TrendingDown className="mr-0.5 inline h-3 w-3 text-green-400" />
                  Excellent &lt;{benchmark.excellent_threshold}
                </span>
                <span>
                  <Minus className="mr-0.5 inline h-3 w-3 text-blue-400" />
                  Good &lt;{benchmark.good_threshold}
                </span>
                <span>Random = {benchmark.random_guess}</span>
              </span>
            </div>
          ) : null}

          {/* Resolution status footer */}
          {status?.status === "active" ? (
            <div className="flex items-center justify-between text-[0.68rem] text-white/30">
              <span>
                {status.resolved_count} resolved
                {status.voided_count > 0 ? ` / ${status.voided_count} voided` : ""}
              </span>
              <span>{summary?.period_days ?? 30}-day window</span>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
